<template>
	<template v-if="entry && !isHiddenOnThisPage">
		<Popover v-model:open="isOpen">
			<PopoverTrigger as-child>
				<button
					type="button"
					:class="[CHIP_CLASSES, 'hidden md:flex', TONE_CLASSES[tone]]"
					:data-tone="tone"
					data-testid="journey-header-button"
				>
					<SignalIcon
						class="size-5"
						aria-hidden="true"
					/>
					<span class="px-1">{{ entry.chip(journey) }}</span>
					<component
						:is="isOpen ? ChevronUpIcon : ChevronDownIcon"
						class="size-5"
						aria-hidden="true"
					/>
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="end"
				:side-offset="4"
				aria-labelledby="journey-popover-headline"
				class="z-80 w-80 rounded-lg border border-gray-200 bg-white p-3 shadow-[0px_4px_2px_rgba(0,0,0,0.25)] ring-0"
			>
				<FocusPopoverContent
					:journey="journey"
					@close="isOpen = false"
				/>
			</PopoverContent>
		</Popover>

		<button
			type="button"
			:class="[CHIP_CLASSES, 'flex max-md:tap-target md:hidden', TONE_CLASSES[tone]]"
			:aria-label="entry.chip(journey)"
			aria-haspopup="dialog"
			:aria-expanded="isDialogOpen"
			data-testid="journey-header-button-mobile"
			@click="isDialogOpen = true"
		>
			<component
				:is="icon"
				class="size-5"
				aria-hidden="true"
			/>
			<span
				v-if="shortChip"
				class="px-1"
			>{{ shortChip }}</span>
		</button>

		<!-- On the body: the header is its own stacking layer (z-80), under page elements above that. -->
		<Teleport to="body">
			<!-- `aria-labelledby` replaces Modal's own `modal-title`, which nothing here carries. -->
			<Modal
				v-if="isDialogOpen"
				center-on-mobile
				hide-actions
				close-button-absolute
				remove-padding
				:prevent-scrolling="false"
				max-width="sm:max-w-sm"
				rounded-size="rounded-lg"
				aria-labelledby="journey-popover-headline"
				data-testid="journey-dialog"
				@modal:close="isDialogOpen = false"
			>
				<template #body>
					<div class="p-4">
						<FocusPopoverContent
							:journey="journey"
							@close="isDialogOpen = false"
						/>
					</div>
				</template>
			</Modal>
		</Teleport>
	</template>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery } from '@pinia/colada';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid';
import { SignalIcon } from '@heroicons/vue/24/outline';

import Modal from '../modal.vue';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import FocusPopoverContent from './focus-popover-content.vue';
import { focusIconFor, focusStateFor } from './focus-states';

import { getJourney } from '../../api/journey';
import { isYourJourneyEnabled } from '../../composables/your-journey';

/**
 * Today's Your Journey focus, on every page: a popover on desktop (EN-1024), and on a phone a shorter
 * chip that opens the same content as a centred dialog (EN-1162).
 */
// Each button adds its own display: the desktop chip from `md`, the short chip below it.
const CHIP_CLASSES = [
	'h-8 items-center rounded-md bg-gray-100 px-2 py-1 text-p-sm font-medium whitespace-nowrap',
	'hover:bg-gray-200 focus-visible:ring-2 focus-visible:ring-ring',
].join(' ');

// Figma's content/secondary, and content/on-orange-subtle while a diagnosis is in focus.
const TONE_CLASSES = {
	default: 'text-gray-500',
	diagnosis: 'text-orange-600',
};

const route = useRoute();
const isEnabled = isYourJourneyEnabled();
const isOpen = ref(false);
// Its own state, so opening the dialog never opens the desktop popover behind it.
const isDialogOpen = ref(false);

/**
 * Shares the journey query with Your Journey, so a refresh or a Change Focus on either moves both.
 * Each request also starts the user's next 24-hour focus window once the last one has ended.
 */
const { data: journey, refresh } = useQuery({
	key: ['journey'],
	query: getJourney,
	enabled: isEnabled,
});

const entry = computed(() => focusStateFor(journey.value));
const tone = computed(() => (journey.value?.focus.source === 'diagnosis' ? 'diagnosis' : 'default'));
const shortChip = computed(() => entry.value?.shortChip(journey.value));
const icon = computed(() => focusIconFor(journey.value));

// A live InterviewAI session renders the app header, and the button shouldn't pull anyone out mid-recording.
const isHiddenOnThisPage = computed(() => route.name === 'interview-ai-interview');

// A popover or dialog left open while the focus had no header copy would otherwise reappear by itself.
watch(entry, (current) => {
	if (!current) {
		isOpen.value = false;
		isDialogOpen.value = false;
	}
});

// The header outlives page changes, so it asks again on each one: the focus moves as the user acts.
// `refresh` skips a journey fetched in the last few seconds and joins a request already in flight.
watch(() => route.name, () => {
	isOpen.value = false;
	isDialogOpen.value = false;

	if (isEnabled) {
		refresh();
	}
});
</script>
